"use client"
import Link from "next/link";
import Image from "next/image";
import { SbBlokData } from "@storyblok/react/rsc";
import { Star } from "lucide-react";
import StoryblokImage from "./StoryblokImage";

interface StoryblokLink {
    id?: string;
    url?: string;
    linktype: "story" | "url" | "email" | "asset";
    fieldtype: "multilink";
    cached_url?: string;
    anchor?: string;
    target?: string;
}

interface StoryblokAsset {
    filename?: string;
    alt?: string;
}

interface NavItem {
    Label?: string;
    Link?: StoryblokLink;
}

interface FloatingNavProps {
    logo?: StoryblokAsset;
    footerNav?: NavItem[];
    supportNav?: NavItem[];
    CTA?: Array<SbBlokData & {
        Type?: string;
        Label?: string;
        SubLabel?: string;
        NeuesFenster?: boolean;
        Link?: StoryblokLink;
    }>;
    metaDescription?: string;
    socialIcons?: Array<{
        Icon?: StoryblokAsset;
        Link?: StoryblokLink;
        Bezeichnung?: string;
        _uid?: string;
    }>;
}

function getLinkUrl(link?: StoryblokLink): string {
    if (!link) return "/";

    switch (link.linktype) {
        case "story":
            {
                const slug = link.cached_url || "";
                const cleaned = slug.startsWith("pages/")
                    ? slug.slice("pages/".length)
                    : slug === "pages"
                        ? ""
                        : slug;
                return `/${cleaned}`.replace(/\/+/g, "/");
            }
        case "url":
            return link.url || "/";
        case "email":
            return `mailto:${link.url || ""}`;
        case "asset":
            return link.url || "/";
        default:
            return "/";
    }
}

export default function Footer({ logo, footerNav, supportNav, CTA, metaDescription, socialIcons }: FloatingNavProps) {
    const mainCta = CTA && CTA.length ? CTA[0] : null;
    const mainCtaWithType = mainCta ? { ...mainCta, Type: mainCta.Type || 'secondary' } : null;

    return (
        <div className="bg-black rounded-tl-4xl rounded-tr-4xl">
            <div className="container mx-auto p-10 lg:py-20 flex flex-col gap-10 text-center lg:text-start lg:grid lg:grid-cols-6 lg:gap-5 grid-cols-1">
                <div className="flex flex-col gap-3 col-span-2">
                    {logo?.filename ? (
                        <Link href="/" className="flex items-center mx-auto lg:mx-0 gap-2" aria-label="Home">
                            <Image
                                src={logo.filename}
                                alt={logo.alt || "Logo"}
                                width={140}
                                height={40}
                                className="h-8 w-auto"
                                priority
                            />
                        </Link>
                    ) : (
                        <Link href="/" className="text-base font-semibold">
                            basecloud
                        </Link>
                    )}
                    <p className="paragraph">
                        {metaDescription}
                    </p>

                    <div className="flex flex-col items-center lg:items-start gap-4 mt-5">
                        <span className="font-bold text-white">
                            Folge uns:
                        </span>
                        <div className="flex items-center gap-4 flex-wrap">
                            {socialIcons?.map((icon) => {
                                const href = getLinkUrl(icon.Link);
                                const target = icon.Link?.target;
                                const label = icon.Bezeichnung;
                                const iconAsset = icon.Icon?.filename
                                    ? { filename: icon.Icon.filename, alt: icon.Icon.alt }
                                    : null;

                                if (!iconAsset) return null;

                                return (
                                    <Link
                                        key={icon._uid || `${label || 'social'}-${href}`}
                                        href={href}
                                        target={target}
                                        className="flex items-center gap-2 text-slate-200 hover:text-white hover:scale-105 transition-transform"
                                    >
                                        <StoryblokImage
                                            src={iconAsset}
                                            alt={iconAsset.alt || label || "Social Icon"}
                                            width={24}
                                            height={24}
                                            className="size-8 object-contain"
                                        />
                                        <span className="text-sm sr-only">{label || "Social"}</span>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {mainCtaWithType && (
                    <div className="hidden items-center gap-3 md:flex text-white">
                        {mainCtaWithType.Link && mainCtaWithType.Label && (
                            <Link
                                href={getLinkUrl(mainCtaWithType.Link)}
                                target={mainCtaWithType.NeuesFenster ? "_blank" : undefined}
                                rel={mainCtaWithType.NeuesFenster ? "noopener noreferrer" : undefined}
                                className={`main-button main-button--${mainCtaWithType.Type === 'secondary' || mainCtaWithType.Type === 'ghost' ? mainCtaWithType.Type : 'main'}`}
                            >
                                <span className="main-button-label">{mainCtaWithType.Label}</span>
                                {mainCtaWithType.SubLabel && mainCtaWithType.Type !== 'secondary' && (
                                    <span className="main-button-sublabel">{mainCtaWithType.SubLabel}</span>
                                )}
                            </Link>
                        )}
                    </div>
                )}
                <div></div>
                <div></div>

                <div className="flex flex-col gap-3 text-white lg:justify-start lg:items-start lg:text-left">
                    <span className="font-bold text-white">Navigation</span>
                    <div className="flex flex-col gap-2">
                        {footerNav?.map((item, index) => {
                            if (!item?.Label || !item?.Link) return null;

                            return (
                                <Link
                                    key={`${item.Label}-${index}`}
                                    href={getLinkUrl(item.Link)}
                                    target={item.Link.target}
                                    className="text-white transition-colors hover:underline hover:text-primary"
                                >
                                    {item.Label}
                                </Link>
                            );
                        })}
                    </div>
                </div>

                <div className="flex flex-col gap-3 text-white lg:justify-start lg:items-start lg:text-left">
                    <span className="font-bold text-white">Support</span>
                    <div className="flex flex-col gap-2">
                        {supportNav?.map((item, index) => {
                            if (!item?.Label || !item?.Link) return null;

                            return (
                                <Link
                                    key={`${item.Label}-${index}`}
                                    href={getLinkUrl(item.Link)}
                                    target={item.Link.target}
                                    className="text-white transition-colors hover:underline hover:text-primary"
                                >
                                    {item.Label}
                                </Link>
                            );
                        })}
                    </div>
                </div>

                {/* <div className="md:hidden">
                    <FloatingNavMobile logo={logo} items={items} cta={mainCta} />
                </div> */}
            </div>
            <Image height={0} width={0} src="/BASECLOUD_footer.svg" alt="Basecloud" className="w-full" />
        </div>
    );
}
